/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { getTrainingValidation } from 'src/clu/modules/utils/trainingValidationUtils';
import { Page, PageProps } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { TrainingModelDto } from 'src/modules/modelTraining/components/SubmitTrainingJobPanel';
import { TrainingLayout } from 'src/modules/modelTraining/components/TrainingLayout';
import { TrainingAllowanceData } from 'src/modules/modelTraining/types/trainingTypes';

const messages = defineMessages({
    pageTitle: {
        id: 'clu.cluTrainingPage.pageTitle',
        defaultMessage: 'Training jobs'
    },
    noUtterances: {
        id: 'clu.cluTrainingPage.noUtterances',
        defaultMessage: 'There are no utterances in your project. Please add and label utterances to train a model.'
    },
    emptyProject: {
        id: 'clu.cluTrainingPage.emptyProject',
        defaultMessage: 'There is no data in your project.'
    },
    nonSufficientUtterances: {
        id: 'clu.cluTrainingPage.nonSufficientUtterances',
        defaultMessage: `You have not met the recommended minimum number of 10 utterances in your project
 before training. Please add more utterances using the Label page.`
    },
    trainingSettingsTitle: {
        id: 'clu.cluTrainingPage.trainingSettingsTitle',
        defaultMessage: 'Training settings'
    },
    pageDescription: {
        id: 'clu.cluTrainingPage.pageDescription',
        defaultMessage:
            'After labeling your data, start a training job to create a model that understands conversational utterances. Select the trained model from a successful job to view its performance results. Jobs from the last 7 days are displayed here.'
    },
    userDefinedOptionDisabledTooltip: {
        id: 'clu.cluTrainingPage.userDefinedOptionDisabledTooltip',
        defaultMessage:
            'Manual splitting isn’t available because there aren’t any utterances in the testing set yet. To enable manual splitting, add utterances to the testing set in Data Labeling page.'
    }
});

const { styleComponent } = getStylistV2('CluTrainingPage');
const StyledPage = styleComponent<PageProps>(Page)('StyledPage', { position: 'relative' });

export const CluTrainingPage = observer(() => {
    const { rootStore, dispatcher, uiStore } = useGlobalState();
    const { localize } = useLocalization();
    const history = useHistory();

    const { cluGlobalsStoreHouse, currentCluProjectStore } = rootStore;
    const currentProject = currentCluProjectStore;
    const { utterances } = currentProject.jsonData;
    const modelNames = currentProject.trainedModelStoreHouse.items.map(model => model.name) ?? [];
    const routes = routeTable[currentProject.projectType === 'Conversation' ? 'clu' : 'orchestration'];
    const lessThanRecommendedUtterances = utterances.length < LuisConstants.Clu.MinimumNumberOfLabeledUtterances;
    const isOrchestrationOnlyIntents = currentProject.intentStoreHouse.items
        .filter(i => i.name !== LuisConstants.Clu.NoneIntentName)
        .every(i => Boolean(i.orchestrationInfo));

    const isEmptyProject =
        utterances.length === 0 &&
        currentProject.jsonData.intents.filter(i => i.category !== LuisConstants.Clu.NoneIntentName).length === 0 &&
        currentProject.jsonData.entities.length === 0;

    const isTrainingAllowed: TrainingAllowanceData = getTrainingValidation(currentProject, localize);
    const { trainingConfigVersions } = cluGlobalsStoreHouse.findById(currentProject.projectType);

    React.useEffect(() => {
        uiStore.setTeachingBubble('CluTrainModelPage', true);
    }, []);

    const train = (model: TrainingModelDto) => dispatcher.dispatch('trainCluModel', { model, toastData: {} });

    const cancelTraining = (jobId: string) => dispatcher.dispatch('cancelCluTraining', { jobId });

    const onRenderWarningsSection = () => (
        <>
            {!isOrchestrationOnlyIntents && currentProject.jsonData.utterances.length === 0 && (
                <MessageBar messageBarType={MessageBarType.severeWarning}>{localize(messages.noUtterances)}</MessageBar>
            )}

            {isEmptyProject && <MessageBar messageBarType={MessageBarType.severeWarning}>{localize(messages.emptyProject)}</MessageBar>}

            {!isOrchestrationOnlyIntents && lessThanRecommendedUtterances && (
                <MessageBar messageBarType={MessageBarType.warning}>{localize(messages.nonSufficientUtterances)}</MessageBar>
            )}
        </>
    );

    return (
        <StyledPage size="container-fluid" pageName="TrainingPage" title={localize(messages.pageTitle)}>
            <TrainingLayout
                onFireTraining={train}
                trainedModelNames={modelNames}
                onCancelTraining={cancelTraining}
                trainingAllowance={isTrainingAllowed}
                projectType={currentProject.projectType}
                warningsSection={onRenderWarningsSection()}
                learnMoreLink="https://aka.ms/clu-data-split"
                trainingConfigVersions={trainingConfigVersions}
                pageDescription={localize(messages.pageDescription)}
                tagDataLink={routes.dataTaggingPage(currentProject.id)}
                trainingJobs={currentProject.trainingJobStoreHouse.items}
                userDefinedTestingPercentage={currentProject.testingSetPercentage}
                isLoading={currentProject.trainingJobStoreHouse.tracker.isRunning}
                advancedTrainingPricingLink="https://aka.ms/unifiedLanguagePricing"
                userDefinedTrainingPercentage={currentProject.trainingSetPercentage}
                currentTrainingModelName={currentProject.currentTrainingJob?.modelId}
                showTrainingModeChoices={currentProject.projectType === 'Conversation'}
                isTrainingInProgress={currentProject.trainingServiceStatus !== 'ready'}
                isEvaluationInProgress={
                    currentProject.evaluationJobStore &&
                    currentProject.evaluationJobStore?.status !== 'succeeded' &&
                    currentProject.evaluationJobStore?.status !== 'failed'
                }
                isUserDefinedSplittingEnabled={Boolean(currentProject.testingSetPercentage)}
                userDefinedOptionDisabledTooltip={localize(messages.userDefinedOptionDisabledTooltip)}
                onModelClick={modelId => history.push(routes.modelDetailsPage(currentProject.id, modelId))}
            />
        </StyledPage>
    );
});
